import {
  Box,
  Button,
  Flex,
  Icon,
  ReadMoreMarkdown,
  SortDirection,
  Spinner,
  Table,
  TableColumnType,
  Text,
  Tooltip,
} from '@fullstory/components';
import { CellAlignment } from '@fullstory/components/legacy/Table/types';
import { intl } from '@fullstory/intl';
import { ObjectType } from '@fullstory/proto/fs/services/dx-objects';
import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { InsightsActionMenu } from '../InsightsActionMenu';
import { ResourceListItemDescription } from '../ResourceList/ResourceListItemDescription';
import { StarToggle } from '../StarToggle';
import {
  LIBRARY_TABLE_ACTIONS_COLUMN_KEY,
  LIBRARY_TABLE_CREATOR_COLUMN_KEY,
  LIBRARY_TABLE_INSIGHTS_COLUMN_KEY,
  LIBRARY_TABLE_LAST_MODIFIED_COLUMN_KEY,
  LIBRARY_TABLE_NAME_COLUMN_KEY,
  LIBRARY_TABLE_SORTABLE_COLUMNS,
  LIBRARY_TABLE_SPACE_COLUMN_KEY,
} from './constants';
import { FilterOptionValueType } from '@fullstory/redux';
import { Avatar } from '../Avatar';
import { SpaceLinkIndicator } from '../SpaceLinkIndicator';
import { LibraryObjectType } from '@fullstory/routes';
import { LibraryObject, Sort } from './types';
import { LibraryObjectTooltip } from '../LibraryObjectTooltip';
import { VisibilityLabel } from '../VisibilityLabel';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import { useFlags } from '@fullstory/react-hooks';
import { BulkActionTable } from '../BulkActionTable';
import { FormattedMessage } from 'react-intl';
import { ConfirmActionType, ConfirmModal } from '@fullstory/junk-drawer-ui';
import { SpaceObjectModalContainer } from '../../containers';
import { useSpaceOptions } from '../../hooks';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { assertExhaustive } from '@fullstory/typeutils';
import { SpaceActionType } from '../../types';
import { int64 } from '@fullstory/proto/google/protobuf';
import { BulkAction } from '../BulkActionTable/bulkActions';
import { useLibraryBulkActions, BulkActionIds } from '../../hooks/useLibraryBulkActions';
import { CannedMetricCreator } from '@fullstory/client-api/goimports/fs_services_webber_internal_saved_metrics';
import { CannedSegmentCreator } from '@fullstory/client-api';

const icons: {
  [type in LibraryObjectType]: React.ComponentProps<typeof ResourceListItemDescription>['icon'];
} = {
  [ObjectType.DASHBOARD]: 'dashboard',
  [ObjectType.FUNNEL]: 'funnels',
  [ObjectType.HEATMAP]: 'heatmap',
  [ObjectType.JOURNEY]: 'journeys',
  [ObjectType.METRIC]: 'metrics',
  [ObjectType.RETENTION]: 'retention',
  [ObjectType.SEGMENT]: 'segments',
  [ObjectType.DATA_TABLE]: 'data-table',
};

const keyAndSortColumnProps = (key: string) => ({
  key,
  sortable: LIBRARY_TABLE_SORTABLE_COLUMNS.includes(key),
});

/**
 * Small helper component to keep the Text styling consistent with disabling, truncating, etc.
 */
const TableText: React.FC<PropsWithChildren<{ object: LibraryObject }>> = ({
  object,
  children,
}) => (
  <Text
    data-component="TableText"
    appearance="bodySmall"
    noWrap
    color={object.archived ? 'disabled' : undefined}
  >
    {children}
  </Text>
);

interface LibraryTableProps {
  /**
   * When objects exist but results are being refetched.
   *
   * Shows an indicator on the table.
   */
  refetching: boolean;
  objects: LibraryObject[];
  sort: Sort;
  onSortChange: (sort: Sort) => void;
  currentSpaceId: string | null | undefined;
  fillParent?: boolean;
  filter?: FilterOptionValueType;
  /**
   * Adds the 3-dot menu actions column to the table.
   *
   * @default true
   */
  showActions?: boolean;
  /**
   * Adds the Archive, Restore, Delete, Transfer, Add, and Remove bulk actions to the table.
   *
   * @default false
   */
  showBulkActions?: boolean;
  /**
   * Adds the Creator column to the table.
   *
   * @default true
   */
  showCreator?: boolean;
  /**
   * Adds the Recent Insights column to the table.
   *
   * @default true
   */
  showInsights?: boolean;
  /**
   * Adds the Space link avatars into the Table when the object is linked.
   *
   * @default true
   */
  showSpaceLinks?: boolean;
  /**
   * Adds the starred icon and actions to the table.
   *
   * @default true
   */
  showStarred?: boolean;
  /**
   * Render prop function for the far right or far left column actions.
   */
  renderActions: (object: LibraryObject) => React.ReactNode;
  /**
   * Optionally provide the placement of the actions column in the table.
   *
   * @default 'right'
   */
  actionsColumnPlacement?: 'left' | 'right';
  /**
   * Optionally provide the width of the actions column in the table. This is useful to
   * tighten up the width of this column, and work with different action button text content lengths.
   *
   * @default undefined
   */
  actionsColumnWidth?: string | number | undefined;
  /**
   * The view action. A value of undefined means that rows in the table will not be clickable.
   */
  onView?: (object: LibraryObject) => void;
  /**
   * Starring actions. A value of undefined means that starring actions are not available,
   * though a Star icon indicating Starred status is still shown.
   *
   * Will not be displayed if `showStarred` is false.
   */
  starActions?: {
    star: (object: LibraryObject) => void;
    unstar: (object: LibraryObject) => void;
  };
  /**
   * If the object can display a preview definition tooltip, use this function to build the props.
   *
   * If not provided or returns undefined, the object description string will be displayed in a Tooltip if it exists.
   */
  getLibraryObjectTooltipProps?: (
    object: LibraryObject,
  ) => React.ComponentProps<typeof LibraryObjectTooltip> | undefined;

  refetch?: () => void;
  actionMenuOpenObjectId?: string | null;

  additionalColumns?: TableColumnType<LibraryObject>[];
}

type ActionType = 'archive' | 'restore' | 'delete' | 'transfer' | 'add' | 'remove';

interface BulkActionFields {
  mainText: string;
  subText: string;
  action: ConfirmActionType;
}

const getConfirmationTextFields = (action: () => void, type: ActionType, numItems: number) => {
  const fields: BulkActionFields = {
    mainText: '',
    subText: '',
    action: {
      action,
      variant: 'fill-destructive',
      text: '',
    },
  };
  switch (type) {
    case 'archive':
      fields.mainText = intl.formatMessage(
        {
          defaultMessage:
            'Only {numItems} {numItems, plural, one {item} other {items}} will be archived.',
          id: 'L6I1w+',
        },
        { numItems },
      );
      fields.subText = intl.formatMessage({
        defaultMessage: 'Any dashboards or system generated items selected will not be archived.',
        id: 'OGqs/B',
      });
      fields.action.text = intl.formatMessage({
        defaultMessage: 'Archive',
        id: 'hrgo+E',
      });
      break;
    case 'restore':
      fields.mainText = intl.formatMessage(
        {
          defaultMessage:
            'Only {numItems} {numItems, plural, one {item} other {items}} will be restored.',
          id: '4ZjGLF',
        },
        { numItems },
      );
      fields.subText = intl.formatMessage({
        defaultMessage:
          'All of the items you have selected that are not dashboards will be restored.',
        id: 'LicF5A',
      });
      fields.action.text = intl.formatMessage({
        defaultMessage: 'Restore',
        id: 'zz6ObK',
      });
      fields.action.variant = 'fill-primary';
      break;
    case 'delete':
      fields.mainText = intl.formatMessage(
        {
          defaultMessage:
            'This will only delete the {numItems} {numItems, plural, one {dashboard} other {dashboards}} you selected.',
          id: 'Rkcmb7',
        },
        { numItems },
      );
      fields.subText = intl.formatMessage({
        defaultMessage:
          'Only user-created dashboards can be deleted. All other items that you have selected will not be affected.',
        id: 'wFWc8d',
      });
      fields.action.text = intl.formatMessage({
        defaultMessage: 'Delete',
        id: 'K3r6DQ',
      });
      break;
    case 'remove':
      fields.mainText = intl.formatMessage(
        {
          defaultMessage:
            'Only {numItems} {numItems, plural, one {item} other {items}} will be removed from this space.',
          id: 'Xz2Tel',
        },
        { numItems },
      );
      fields.subText = intl.formatMessage({
        defaultMessage: 'Items that belong to this space will not be removed.',
        id: '8aOxFs',
      });
      fields.action.text = intl.formatMessage({
        defaultMessage: 'Remove',
        id: 'G/yZLu',
      });
      break;
    case 'transfer':
    case 'add':
      // for 'transfer' and 'add', since we aren't using the same confirmation modal, we don't need the extra text fields.
      break;
    default:
      assertExhaustive(type);
  }

  return fields;
};

export const LibraryTable: React.FC<LibraryTableProps> = ({
  refetching = false,
  objects,
  sort,
  onSortChange,
  currentSpaceId,
  fillParent = false,
  filter,
  showActions = true,
  showBulkActions = false,
  showCreator = true,
  showInsights = true,
  showSpaceLinks = true,
  showStarred = true,
  renderActions,
  actionsColumnWidth = undefined,
  actionsColumnPlacement = 'right',
  onView,
  starActions,
  getLibraryObjectTooltipProps,
  refetch = () => {},
  actionMenuOpenObjectId,
  additionalColumns = [],
}) => {
  const flags = useFlags();
  const allowBulkActions = flags.has(Flag.PermModifyLibraryObjects) && showBulkActions;
  const handleOnClick = (object: LibraryObject) => {
    if (object.archived || !onView) return;
    onView(object);
  };
  const [confirmBulkAction, setConfirmBulkAction] = useState<BulkActionFields | null>(null);

  const [spaceObjectModalOpen, setSpaceObjectModalOpen] = useState(false);
  const [keepReference, setKeepReference] = useState(false);

  const {
    bulkArchive,
    bulkRestore,
    bulkTransfer,
    bulkReference,
    bulkDereference,
    bulkDeleteDashboards,
  } = useLibraryBulkActions();

  const [bulkActionIds, setBulkActionIds] = useState<BulkActionIds | null>(null);
  const [spaceActionType, setSpaceActionType] = useState<SpaceActionType | null>(null);

  const { options, selected, setSelected } = useSpaceOptions(
    currentSpaceId,
    spaceActionType ?? 'transfer',
    true,
    false,
  );

  useEffect(() => {
    if (bulkActionIds !== null) {
      if (spaceActionType === 'transfer') {
        setBulkActionIds(null);
        bulkTransfer(bulkActionIds, selected?.value ?? '', keepReference).finally(() => {
          setSpaceActionType(null);
          setConfirmBulkAction(null);
          setKeepReference(false);
          refetch();
        });
      } else if (spaceActionType === 'add') {
        setBulkActionIds(null);
        bulkReference(bulkActionIds, selected?.value ?? '').finally(() => {
          setSpaceActionType(null);
          setConfirmBulkAction(null);
          setKeepReference(false);
          refetch();
        });
      }
    }
  }, [
    bulkTransfer,
    bulkActionIds,
    keepReference,
    refetch,
    selected?.value,
    spaceActionType,
    bulkReference,
  ]);

  // because TypeScript :/
  const leftAlign: CellAlignment = 'left';
  const rightAlign: CellAlignment = 'right';
  const descSortDirection: SortDirection = 'desc';

  const columns: TableColumnType<LibraryObject>[] = useMemo(() => {
    const actionsColumn = {
      ...keyAndSortColumnProps(LIBRARY_TABLE_ACTIONS_COLUMN_KEY),
      align: actionsColumnPlacement === 'left' ? leftAlign : rightAlign,
      width: actionsColumnWidth,
      renderCell(object: LibraryObject) {
        return renderActions(object);
      },
    };
    return [
      ...(showActions && actionsColumnPlacement === 'left' ? [actionsColumn] : []),
      {
        ...keyAndSortColumnProps(LIBRARY_TABLE_NAME_COLUMN_KEY),
        label: intl.formatMessage({ defaultMessage: 'Name', id: 'HAlOn1' }),
        align: 'left',
        width: '35%',
        minWidth: 200,
        renderCell(object: LibraryObject) {
          const name = <TableText object={object}>{object.name}</TableText>;
          const nameTooltipProps = getLibraryObjectTooltipProps?.(object);

          return (
            <Flex justifyContent="flex-start" alignItems="center" gap={12}>
              <Box flexShrink={0}>
                <Icon
                  name={icons[object.objectType]}
                  size={20}
                  color={object.archived ? 'disabled' : 'secondary'}
                />
              </Box>
              {showSpaceLinks && (
                <SpaceLinkIndicator objectType={object.objectType} space={object.owningSpace} />
              )}
              <Box overflow="hidden">
                {nameTooltipProps ? (
                  <LibraryObjectTooltip {...nameTooltipProps} showDelay={300} placement="right">
                    {name}
                  </LibraryObjectTooltip>
                ) : object.description ? (
                  <Tooltip
                    tooltipContent={
                      <ReadMoreMarkdown
                        maxLines={2}
                        content={object.description}
                        ignoreMultilineComponents
                      />
                    }
                  >
                    {name}
                  </Tooltip>
                ) : (
                  name
                )}
              </Box>
              <VisibilityLabel
                visibility={object.visibility}
                tooltipContent={intl.formatMessage({
                  defaultMessage: 'This object is only visible to you.',
                  id: 'd6ciTa',
                })}
              />
              {showStarred && (
                <Box>
                  {starActions ? (
                    <StarToggle
                      starred={object.starred}
                      onClick={evt => {
                        evt.stopPropagation();
                        object.starred ? starActions.unstar(object) : starActions.star(object);
                      }}
                      size={20}
                      disabled={object.archived}
                    />
                  ) : (
                    <Icon
                      size={16}
                      name={object.starred ? 'star-filled' : 'star-unfilled'}
                      color={object.starred ? 'favorite' : 'secondary'}
                    />
                  )}
                </Box>
              )}
            </Flex>
          );
        },
      },
      ...additionalColumns,
      ...(showInsights
        ? [
            {
              ...keyAndSortColumnProps(LIBRARY_TABLE_INSIGHTS_COLUMN_KEY),
              label: intl.formatMessage({ defaultMessage: 'Recent Insights', id: 'v08soM' }),
              align: leftAlign,
              width: 150,
              initialSortDirection: descSortDirection,
              tooltip: intl.formatMessage({
                defaultMessage: 'Insights generated within the past 7 days',
                id: 'VdQfK9',
              }),
              // We have a filter to see just objects with recent insights, but do not sort on that field.
              sortable: false,
              renderCell(object: LibraryObject) {
                if (!object.insights.length) return null;

                return (
                  <InsightsActionMenu
                    data-purpose="metrics-insights-recent-insights-count"
                    insights={object.insights}
                  />
                );
              },
            },
          ]
        : []),
      ...(!currentSpaceId
        ? [
            {
              ...keyAndSortColumnProps(LIBRARY_TABLE_SPACE_COLUMN_KEY),
              label: intl.formatMessage({ defaultMessage: 'Space', id: '0ah2ud' }),
              minWidth: 200,
              renderCell(object: LibraryObject) {
                const { owningSpace: space } = object;

                if (!space || object.visibility === Visibility.PRIVATE) return null;

                return (
                  <Flex gap={8} justifyContent="flex-start">
                    <Avatar size="small" color={space.color} />
                    <TableText object={object}>{space.name}</TableText>
                  </Flex>
                );
              },
            },
          ]
        : []),
      ...(showCreator
        ? [
            {
              ...keyAndSortColumnProps(LIBRARY_TABLE_CREATOR_COLUMN_KEY),
              label: intl.formatMessage({ defaultMessage: 'Creator', id: 'STGKH9' }),
              align: leftAlign,
              minWidth: 100,
              renderCell(object: LibraryObject) {
                return (
                  <TableText object={object}>
                    {/* Do not need to intl Fullstory */}
                    {object.createdBy === CannedSegmentCreator ||
                    object.createdBy == CannedMetricCreator
                      ? // eslint-disable-next-line @fullstory/no-rawtext-in-jsx-element-return
                        'Fullstory'
                      : object.createdBy}
                  </TableText>
                );
              },
            },
          ]
        : []),
      {
        ...keyAndSortColumnProps(LIBRARY_TABLE_LAST_MODIFIED_COLUMN_KEY),
        label: intl.formatMessage({ defaultMessage: 'Last modified', id: '1Jufsz' }),
        align: leftAlign,
        minWidth: 100,
        renderCell(object: LibraryObject) {
          return (
            <TableText object={object}>
              {object.lastUpdated
                ? intl.formatDate(object.lastUpdated as string, {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : ''}
            </TableText>
          );
        },
      },
      ...(showActions && actionsColumnPlacement === 'right' ? [actionsColumn] : []),
    ];
  }, [
    actionsColumnPlacement,
    actionsColumnWidth,
    showActions,
    additionalColumns,
    showInsights,
    currentSpaceId,
    showCreator,
    renderActions,
    getLibraryObjectTooltipProps,
    showSpaceLinks,
    showStarred,
    starActions,
  ]);

  const bulkActions: BulkAction<LibraryObject>[] = useMemo(
    () => [
      {
        key: 'add',
        action: (rows, allRowsSelected) => {
          const ids = populateBulkActionIds({
            rows,
            includeDashboards: true,
            includeNonDashboards: true,
          });
          setBulkActionIds(ids);
        },
        render(action, rows) {
          const numSelected = [...rows.values()].filter(
            r => r.visibility !== Visibility.PRIVATE,
          ).length;

          if (!currentSpaceId || numSelected === 0 || filter === 'archived') {
            return null;
          }

          return (
            <Button
              onClick={() => {
                setConfirmBulkAction(getConfirmationTextFields(action, 'add', numSelected));
                setSpaceActionType('add');
                setSpaceObjectModalOpen(true);
              }}
              variant="minimal"
              iconLeft="plus-circle"
              disabled={numSelected === 0}
            >
              <FormattedMessage defaultMessage="Add" id="2/2yg+" />
            </Button>
          );
        },
      },
      {
        key: 'remove',
        action(rows, allRowsSelected) {
          const ids = populateBulkActionIds({
            rows,
            includeDashboards: true,
            includeNonDashboards: true,
            excludeSpace: currentSpaceId ?? '',
          });
          bulkDereference(ids, currentSpaceId ?? '').finally(() => {
            setConfirmBulkAction(null);
            refetch();
          });
        },
        render(action, rows) {
          const numSelected = [...rows.values()].filter(
            r => r.owningSpace?.id != currentSpaceId && r.visibility !== Visibility.PRIVATE,
          ).length;

          if (!currentSpaceId || numSelected === 0) {
            return null;
          }

          return (
            <Button
              onClick={() => {
                setConfirmBulkAction(getConfirmationTextFields(action, 'remove', numSelected));
              }}
              variant="minimal"
              iconLeft="minus-circle"
              disabled={numSelected === 0}
            >
              <FormattedMessage defaultMessage="Remove" id="G/yZLu" />
            </Button>
          );
        },
      },
      {
        key: 'transfer',
        action: (rows, allRowsSelected) => {
          const ids = populateBulkActionIds({
            rows,
            includeDashboards: true,
            includeNonDashboards: true,
          });
          setBulkActionIds(ids);
        },
        render(action, rows) {
          const numSelected = [...rows.values()].filter(
            r => r.visibility !== Visibility.PRIVATE,
          ).length;

          if (numSelected === 0 || filter === 'archived') {
            return null;
          }

          return (
            <Button
              onClick={() => {
                setConfirmBulkAction(getConfirmationTextFields(action, 'transfer', numSelected));
                setSpaceActionType('transfer');
                setSpaceObjectModalOpen(true);
              }}
              variant="minimal"
              iconLeft="arrow-curve"
              disabled={numSelected === 0}
            >
              <FormattedMessage defaultMessage="Transfer" id="DtYelJ" />
            </Button>
          );
        },
      },
      {
        key: 'archive',
        action(rows, allRowsSelected) {
          const ids = populateBulkActionIds({
            rows,
            includeDashboards: false,
            includeNonDashboards: true,
            includePrivate: true,
            includeCanned: false,
          });
          bulkArchive(ids, rows.size).finally(() => {
            setConfirmBulkAction(null);
            refetch();
          });
        },
        render(action, rows) {
          // only allow archive if non-dashboard is selected
          const numSelected = [...rows.values()].filter(
            v => v.objectType !== ObjectType.DASHBOARD && !v.canned,
          ).length;

          if (numSelected === 0 || filter === 'archived') {
            return null;
          }

          return (
            <Button
              onClick={() => {
                setConfirmBulkAction(getConfirmationTextFields(action, 'archive', numSelected));
              }}
              variant="minimal"
              iconLeft="trash"
            >
              <FormattedMessage defaultMessage="Archive" id="hrgo+E" />
            </Button>
          );
        },
      },
      {
        key: 'restore',
        action(rows, allRowsSelected) {
          const ids = populateBulkActionIds({
            rows,
            includeDashboards: false,
            includeNonDashboards: true,
          });
          bulkRestore(ids, rows.size).finally(() => {
            setConfirmBulkAction(null);
            refetch();
          });
        },
        render(action, rows) {
          // only allow archive if non-dashboard is selected
          const numSelected = [...rows.values()].filter(
            v => v.objectType !== ObjectType.DASHBOARD,
          ).length;

          if (numSelected === 0 || filter !== 'archived') {
            return null;
          }

          return (
            <Button
              onClick={() => {
                setConfirmBulkAction(getConfirmationTextFields(action, 'restore', numSelected));
              }}
              variant="minimal"
              iconLeft="folder"
            >
              <FormattedMessage defaultMessage="Restore" id="zz6ObK" />
            </Button>
          );
        },
      },
      {
        key: 'delete',
        action(rows, allRowsSelected) {
          const ids = [...rows.values()].reduce((acc: int64[], v: LibraryObject) => {
            const d = v.dashboard;
            if (d && !d?.isCanned) {
              acc.push(d.id);
            }
            return acc;
          }, []);
          bulkDeleteDashboards(ids, rows.size).finally(() => {
            setConfirmBulkAction(null);
            refetch();
          });
        },
        render(action, rows) {
          const numSelected = [...rows.values()].filter(
            r => r.objectType === ObjectType.DASHBOARD && !r.canned,
          ).length;

          if (numSelected === 0) {
            return null;
          }

          return (
            <Button
              onClick={() => {
                setConfirmBulkAction(getConfirmationTextFields(action, 'delete', numSelected));
              }}
              variant="minimal"
              iconLeft="trash"
              disabled={numSelected === 0}
            >
              <FormattedMessage defaultMessage="Delete" id="K3r6DQ" />
            </Button>
          );
        },
      },
    ],
    [
      bulkArchive,
      bulkDeleteDashboards,
      bulkDereference,
      bulkRestore,
      currentSpaceId,
      filter,
      refetch,
    ],
  );

  return (
    <Box
      maxWidth={1}
      overflowX="auto"
      overflowY="auto"
      style={{ position: 'relative' }}
      flexGrow={fillParent ? 1 : 0}
      flexShrink={1}
    >
      {allowBulkActions ? (
        <BulkActionTable<LibraryObject>
          resourceType="DxObject"
          data-component="LibraryTable"
          rows={objects}
          rowActions={[]}
          ignoreScroll
          fixed
          dense
          headProps={{ variant: 'dark', rowProps: { cellProps: { fixed: true } } }}
          onRowClick={handleOnClick}
          rowKeyAccessor={o => o.id}
          activeSortColumnKey={sort.sortColumnKey}
          activeSortDirection={sort.sortDirection}
          onSortChange={(sortColumnKey: string, sortDirection: SortDirection) =>
            onSortChange({ sortColumnKey, sortDirection })
          }
          columns={columns}
          isSelectable={(obj: LibraryObject) =>
            !refetching &&
            obj.canOwn &&
            !(actionMenuOpenObjectId && actionMenuOpenObjectId != obj.id)
          }
          bulkActions={bulkActions}
          filters={[]}
          minTableHeight={72} // default value is too large for a single result and breaks the table borders
        />
      ) : (
        <Table
          data-component="LibraryTable"
          rows={objects}
          fixed
          dense
          headProps={{ variant: 'dark', rowProps: { cellProps: { fixed: true } } }}
          bodyProps={onView ? { rowProps: { onClick: handleOnClick } } : undefined} // TODO - can this be done with a proper semantic link instead?
          rowKeyAccessor={o => o.id}
          activeSortColumnKey={sort.sortColumnKey}
          activeSortDirection={sort.sortDirection}
          onSortChange={(sortColumnKey: string, sortDirection: SortDirection) =>
            onSortChange({ sortColumnKey, sortDirection })
          }
          columns={columns}
        />
      )}
      {refetching && (
        <Flex
          justifyContent="center"
          alignItems="center"
          // overlays the table
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 1,
            zIndex: 1000,
          }}
        >
          <Spinner diameter={48} />
        </Flex>
      )}

      <ConfirmModal
        title={intl.formatMessage({ defaultMessage: 'Are you sure?', id: '2oCaym' })}
        confirmAction={confirmBulkAction ? confirmBulkAction.action : () => {}}
        onHide={() => {
          setConfirmBulkAction(null);
        }}
        open={confirmBulkAction != null && confirmBulkAction.mainText !== ''}
      >
        <Text appearance="bodyMediumBold">{confirmBulkAction?.mainText}</Text>
        <Text appearance="bodyMedium">{confirmBulkAction?.subText}</Text>
      </ConfirmModal>
      {spaceActionType != null && (
        <SpaceObjectModalContainer
          onCloseSignal={() => setSpaceObjectModalOpen(false)}
          open={spaceObjectModalOpen}
          onSave={() => {
            if (confirmBulkAction?.action !== null && confirmBulkAction?.action !== undefined) {
              confirmBulkAction.action.action();
            }
          }}
          objectType={ObjectType.UNKNOWN}
          objectOwningSpaceId=""
          selectedSpace={selected}
          onSpaceSelect={setSelected}
          spaceOptions={options}
          selectedSection={null}
          onSectionSelect={() => {}}
          sectionRequired={false}
          sectionOptions={null}
          keepReference={keepReference}
          onKeepReferenceChange={b => setKeepReference(b)}
          mode={spaceActionType}
        />
      )}
    </Box>
  );
};

const populateBulkActionIds = (props: PopulateBulkActionIdsProps): BulkActionIds => {
  const {
    rows,
    includeDashboards,
    includeNonDashboards,
    excludeSpace = '',
    includePrivate = false,
    includeCanned = true,
  } = props;
  const ids: BulkActionIds = {
    funnels: [],
    heatmaps: [],
    journeys: [],
    metrics: [],
    retentionMetrics: [],
    segments: [],
    dashboards: [],
    dataTables: [],
  };
  rows.forEach(r => {
    // don't include canned objects if action does not allow for it
    if (!includeCanned && r.canned) return;
    // don't append private vals to any bulk action.
    if (!includePrivate && r.visibility === Visibility.PRIVATE) return;
    // ignore items in this space
    if (excludeSpace !== '' && r.owningSpace?.id === excludeSpace) return;

    if (includeDashboards && r.objectType === ObjectType.DASHBOARD) {
      ids.dashboards.push(r.id);
    }
    if (includeNonDashboards) {
      switch (r.objectType) {
        case ObjectType.FUNNEL:
          ids.funnels.push(r.id);
          break;
        case ObjectType.HEATMAP:
          ids.heatmaps.push(r.id);
          break;
        case ObjectType.JOURNEY:
          ids.journeys.push(r.id);
          break;
        case ObjectType.METRIC:
          ids.metrics.push(r.id);
          break;
        case ObjectType.RETENTION:
          ids.retentionMetrics.push(r.id);
          break;
        case ObjectType.SEGMENT:
          ids.segments.push(r.id);
          break;
        case ObjectType.DATA_TABLE:
          ids.dataTables.push(r.id);
          break;
      }
    }
  });
  return ids;
};

interface PopulateBulkActionIdsProps {
  rows: Set<LibraryObject>;
  includeDashboards: boolean;
  includeNonDashboards: boolean;
  excludeSpace?: string;
  includePrivate?: boolean;
  includeCanned?: boolean;
}
